import type { Availability, BlockPhase, Split } from '@cadencia/contracts';
import type { RepTarget, WeekIntent } from '../load/types.js';
import {
  buildBlock,
  type BlockTemplate,
  type BuildBlockInput,
  type BuiltBlock,
  type WeekParameters,
} from './block-builder.js';
import { PLANNED_WEEKS_BY_PHASE } from './block-phase.js';
import { READAPTATION_TEMPLATE } from './readaptation-block.js';
import { DEFAULT_SPLIT, hasTemplates, sessionsForDays, sessionsForSplit } from './splits.js';
import type { SessionTemplate } from './block-builder.js';

/**
 * Parametros de semana POR FASE (docs/TRAINING_ENGINE.md §3, §5.1).
 *
 * Aviso de honestidad: el documento solo tabula la readaptacion (§3). Para las
 * otras cinco fases da la intencion —deficit, recomposicion, ganancia— pero no
 * series ni RIR semana a semana. Todo lo que hay debajo de `PHASE_RAMPS` es
 * CONVENCION DECLARADA, escrita para ser discutible y cambiable en un sitio,
 * no un hallazgo. Las unicas restricciones duras que respeta son las que si
 * estan escritas: bandas de volumen (§4.2), limite de ±20 % semanal
 * (`safety/limits.ts`) y deload cada 5-6 semanas de acumulacion (§5.1).
 */

// ---------------------------------------------------------------------------
// Rampas
// ---------------------------------------------------------------------------

export interface WeekRamp {
  /** Series del ejercicio principal: [primera semana, ultima]. */
  mainSets: readonly [number, number];
  accessorySets: readonly [number, number];
  /** RIR objetivo: [primera semana, ultima]. Baja, no sube. */
  rir: readonly [number, number];
  mainReps: readonly [number, number];
  accessoryReps: readonly [number, number];
  /** Semanas iniciales marcadas como reintroduccion. */
  reintroductionWeeks: number;
  /** Ultimas semanas de acumulacion marcadas como intensificacion. */
  intensificationWeeks: number;
  /**
   * Semanas de acumulacion seguidas antes de intercalar un deload (§5.1).
   * `0` = ninguno (bloques de 4 semanas o menos no lo necesitan).
   */
  deloadEveryWeeks: number;
}

export interface PhaseProfile {
  /** Nombre del bloque tal y como lo vera el usuario. */
  label: string;
  defaultSplit: Split;
  maxSessionMinutes: number;
  objectives: readonly string[];
  exitMetrics: readonly string[];
  ramp: WeekRamp;
}

/** Series de una semana de deload respecto a la anterior (§5.2: −40-50 %). */
export const DELOAD_SET_FACTOR = 0.55;

export const PHASE_RAMPS: Record<BlockPhase, PhaseProfile> = {
  readaptation: {
    label: 'Readaptación',
    defaultSplit: 'torso_leg',
    maxSessionMinutes: 90,
    objectives: READAPTATION_TEMPLATE.objectives,
    exitMetrics: READAPTATION_TEMPLATE.exitMetrics,
    // Presente por completitud del registro: la readaptacion usa la tabla
    // explicita del §3, no esta rampa. Ver `weekParamsForPhase`.
    ramp: {
      mainSets: [3, 4],
      accessorySets: [2, 3],
      rir: [4, 2.5],
      mainReps: [8, 10],
      accessoryReps: [10, 12],
      reintroductionWeeks: 2,
      intensificationWeeks: 0,
      deloadEveryWeeks: 0,
    },
  },

  // HEREDADA. Ya no la genera nadie (`SELECTABLE_BLOCK_KINDS`), y sigue aqui
  // porque hay bloques guardados con este valor y tienen que poder pintarse y
  // materializarse. Sus objetivos y metricas se han reescrito en clave de
  // ENTRENAMIENTO: los que habia citaban adherencia nutricional y cintura, y
  // esas dos cosas ya no las mide nadie —una metrica de salida que nadie puede
  // evaluar no es una metrica, es una frase—.
  fat_loss: {
    label: 'Mantener bajo recuperación reducida',
    defaultSplit: 'torso_leg',
    maxSessionMinutes: 90,
    objectives: [
      'Sostener el estímulo sin subir el volumen',
      'Conservar el rendimiento en los patrones principales',
    ],
    exitMetrics: [
      'Sin caída de e1RM >5 % en los patrones principales',
      'Readiness medio estable respecto al inicio del bloque',
    ],
    ramp: {
      // El volumen NO sube: es la rampa de quien entrena con la recuperacion
      // mermada, y añadir series ahi es la via rapida a acumular fatiga sin
      // estimulo util. Se sostiene lo que hay.
      mainSets: [4, 4],
      accessorySets: [3, 3],
      rir: [3, 2],
      mainReps: [6, 10],
      accessoryReps: [10, 15],
      reintroductionWeeks: 0,
      intensificationWeeks: 0,
      deloadEveryWeeks: 5,
    },
  },

  // HEREDADA, igual que `fat_loss`. «Cintura estable o a la baja» se cae por lo
  // mismo: es una metrica de composicion corporal, no de entrenamiento.
  recomposition: {
    label: 'Desarrollo moderado',
    defaultSplit: 'torso_leg',
    maxSessionMinutes: 90,
    objectives: [
      'Progresar en carga en los patrones principales',
      'Acumular volumen dentro de la banda objetivo',
    ],
    exitMetrics: [
      'e1RM al alza en ≥2 patrones principales',
      'Volumen semanal dentro de la banda objetivo en los músculos priorizados',
    ],
    ramp: {
      mainSets: [4, 5],
      accessorySets: [3, 4],
      rir: [3, 1.5],
      mainReps: [6, 10],
      accessoryReps: [10, 12],
      reintroductionWeeks: 0,
      intensificationWeeks: 1,
      deloadEveryWeeks: 5,
    },
  },

  muscle_gain: {
    label: 'Ganancia muscular',
    defaultSplit: 'torso_leg',
    maxSessionMinutes: 90,
    objectives: [
      'Aumentar masa muscular en los músculos priorizados',
      'Acumular volumen progresivo dentro de la banda de cada músculo',
    ],
    exitMetrics: [
      'e1RM al alza en ≥3 patrones principales',
      'Volumen semanal en la mitad alta de la banda sin señales de deload',
      'Ejecución sostenida: RIR declarado dentro de lo prescrito',
    ],
    ramp: {
      // El tope es 5 y no 6 a proposito: con frecuencia 2 y siete huecos por
      // sesion, 6 series por ejercicio principal se sale de la banda maxima del
      // pectoral y la espalda (§4.2) y de los 90 min de sesion.
      mainSets: [4, 5],
      accessorySets: [3, 4],
      rir: [3, 1],
      mainReps: [5, 10],
      accessoryReps: [8, 12],
      reintroductionWeeks: 0,
      intensificationWeeks: 2,
      deloadEveryWeeks: 5,
    },
  },

  /**
   * ESPECIALIZACION (§6 del encargo): desarrollo con el enfasis puesto en los
   * musculos que el usuario ha marcado como prioritarios.
   *
   * QUE LA DIFERENCIA DE `muscle_gain`, Y POR QUE NO ES «LO MISMO PERO MAS»
   *
   * Un bloque de especializacion no sube el volumen total: lo REPARTE. Los
   * musculos prioritarios suben hacia la parte alta de su banda y el resto baja
   * hacia la parte baja de la suya, porque la capacidad de recuperacion es la
   * que es y subir en todo a la vez no es especializar, es simplemente hacer
   * mas. Ese reparto lo ejecuta `volume-fit.ts`, que ya ordena por prioridad;
   * aqui lo que cambia es la rampa.
   *
   * La rampa es la de ganancia con un escalon menos de volumen accesorio: el
   * margen que se libera es el que se le da al musculo priorizado, y la sesion
   * sigue cabiendo en 90 min.
   *
   * Seis semanas y no ocho: un bloque de enfasis mas largo acumula fatiga local
   * en los mismos patrones. El numero es CONVENCION DECLARADA, como el resto de
   * esta tabla, no un hallazgo.
   */
  specialization: {
    label: 'Especialización',
    defaultSplit: 'torso_leg',
    maxSessionMinutes: 90,
    objectives: [
      'Concentrar el volumen en los músculos priorizados',
      'Sostener el resto con el mínimo eficaz',
    ],
    exitMetrics: [
      'Volumen semanal de los músculos priorizados en la mitad alta de su banda',
      'Sin pérdida de e1RM en los patrones no priorizados',
      'Readiness medio estable respecto al inicio del bloque',
    ],
    ramp: {
      mainSets: [4, 5],
      accessorySets: [2, 3],
      rir: [3, 1],
      mainReps: [6, 10],
      accessoryReps: [8, 12],
      reintroductionWeeks: 0,
      intensificationWeeks: 2,
      deloadEveryWeeks: 5,
    },
  },

  maintenance: {
    label: 'Mantenimiento',
    defaultSplit: 'torso_leg',
    maxSessionMinutes: 90,
    objectives: [
      'Sostener masa y rendimiento con el mínimo volumen eficaz',
      'Recuperar adherencia y regularidad',
    ],
    exitMetrics: [
      'Sin pérdida de e1RM >5 %',
      'Sesiones completadas ≥80 % de las programadas',
    ],
    ramp: {
      mainSets: [3, 3],
      accessorySets: [2, 2],
      rir: [3, 3],
      mainReps: [6, 10],
      accessoryReps: [10, 12],
      reintroductionWeeks: 0,
      intensificationWeeks: 0,
      deloadEveryWeeks: 0,
    },
  },

  transition: {
    label: 'Transición',
    defaultSplit: 'torso_leg',
    maxSessionMinutes: 60,
    objectives: [
      'Descargar fatiga acumulada entre dos bloques',
      'Conservar el patrón de entrenamiento sin exigencia',
    ],
    exitMetrics: ['Readiness medio recuperado', 'Agujetas medias ≤2/5'],
    ramp: {
      mainSets: [2, 2],
      accessorySets: [2, 2],
      rir: [4, 4],
      mainReps: [8, 12],
      accessoryReps: [10, 15],
      reintroductionWeeks: 0,
      intensificationWeeks: 0,
      deloadEveryWeeks: 0,
    },
  },
};

// ---------------------------------------------------------------------------
// Generacion de semanas
// ---------------------------------------------------------------------------

const MIN_WEEKS = 1;
const MAX_WEEKS = 16;

function interpolar(desde: number, hasta: number, t: number): number {
  return desde + (hasta - desde) * t;
}

/** El RIR se prescribe en medios: 2,5 es una instruccion, 2,37 no. */
function aMedios(valor: number): number {
  return Math.round(valor * 2) / 2;
}

/**
 * Posiciones (0-based) donde cae un deload.
 *
 * Nunca en la ultima semana del bloque: el cierre de bloque ya trae su propia
 * descarga y su evaluacion, y terminar con dos semanas suaves seguidas convierte
 * el final del bloque en un parentesis.
 */
function semanasDeDeload(total: number, cada: number): ReadonlySet<number> {
  const salida = new Set<number>();
  if (cada <= 0) return salida;
  for (let posicion = cada; posicion < total - 1; posicion += cada + 1) {
    salida.add(posicion);
  }
  return salida;
}

/**
 * Parametros semana a semana de una fase.
 *
 * La readaptacion devuelve la tabla explicita del §3; el resto se genera con la
 * rampa de la fase. La rampa reparte el progreso entre las semanas de
 * ACUMULACION, no entre todas: un deload en medio no debe consumir un escalon
 * de la progresion.
 */
export function weekParamsForPhase(phase: BlockPhase, weeks?: number): WeekParameters[] {
  const perfil = PHASE_RAMPS[phase];
  const pedidas = weeks ?? PLANNED_WEEKS_BY_PHASE[phase];
  const total = Math.min(Math.max(Math.trunc(pedidas), MIN_WEEKS), MAX_WEEKS);

  if (phase === 'readaptation') {
    // La tabla del documento manda. Si se piden mas semanas de las tabuladas se
    // devuelven las que hay: inventar una quinta semana de readaptacion seria
    // extrapolar una fase cuyo limite el documento fija en cuatro.
    return READAPTATION_TEMPLATE.weekParams.slice(0, total).map((semana) => ({ ...semana }));
  }

  const { ramp } = perfil;
  const deloads = semanasDeDeload(total, ramp.deloadEveryWeeks);
  const acumulacion = total - deloads.size;
  const ultimaAcumulacion = acumulacion - 1;

  const salida: WeekParameters[] = [];
  let indiceAcumulacion = 0;
  let anterior: WeekParameters | null = null;

  for (let posicion = 0; posicion < total; posicion += 1) {
    const weekIndex = posicion + 1;

    if (deloads.has(posicion)) {
      // Intensidad mantenida, volumen recortado (§5.2). `anterior` no puede ser
      // null aqui —un deload nunca cae en la posicion 0, porque `cada >= 1`—
      // pero se contempla para no depender de esa invariante a distancia.
      const base = anterior;
      salida.push({
        weekIndex,
        intent: 'deload',
        mainSets: Math.max(1, Math.round((base?.mainSets ?? ramp.mainSets[0]) * DELOAD_SET_FACTOR)),
        accessorySets: Math.max(
          1,
          Math.round((base?.accessorySets ?? ramp.accessorySets[0]) * DELOAD_SET_FACTOR),
        ),
        mainTarget: base?.mainTarget ?? objetivo(ramp.mainReps, ramp.rir[0]),
        accessoryTarget: base?.accessoryTarget ?? objetivo(ramp.accessoryReps, ramp.rir[0]),
      });
      continue;
    }

    const t = ultimaAcumulacion <= 0 ? 0 : indiceAcumulacion / ultimaAcumulacion;
    const rir = aMedios(interpolar(ramp.rir[0], ramp.rir[1], t));

    const semana: WeekParameters = {
      weekIndex,
      intent: intencion(indiceAcumulacion, acumulacion, ramp),
      mainSets: Math.round(interpolar(ramp.mainSets[0], ramp.mainSets[1], t)),
      accessorySets: Math.round(interpolar(ramp.accessorySets[0], ramp.accessorySets[1], t)),
      mainTarget: objetivo(ramp.mainReps, rir),
      accessoryTarget: objetivo(ramp.accessoryReps, rir),
    };

    salida.push(semana);
    anterior = semana;
    indiceAcumulacion += 1;
  }

  return salida;
}

/**
 * Cuantas semanas de acumulacion quedan DESPUES de esta. 0 = es la ultima.
 *
 * POR QUE UNA CUENTA Y NO UNA FRACCION
 *
 * El §11 reserva el drop set para la «fase de acumulacion tardia», y «tardia»
 * se acabo fijando en las DOS ultimas semanas de acumulacion. Eso es una
 * posicion, no una proporcion, y expresarlo como fraccion obliga a que el
 * numero signifique «dos» por casualidad en cada fase: sobre la rampa de
 * ganancia —cinco semanas de acumulacion y siete no-deload— las dos ultimas
 * caen en 0,5 y 0,667, asi que el umbral tendria que ser 0,5; sobre otra rampa
 * con otro reparto de intensificacion, el mismo 0,5 significa tres semanas o
 * una. Contar las que quedan dice «las dos ultimas» en todas.
 *
 * Solo cuenta las semanas de intencion `accumulation`: la reintroduccion va
 * delante y la intensificacion detras, y ninguna de las dos es candidata al
 * drop set, asi que meterlas en la cuenta desplazaria la ventana fuera de donde
 * puede aplicarse.
 *
 * Se deriva de `weekParamsForPhase`, que es determinista, en lugar de guardarse
 * en la fila de la semana: una columna nueva en una tabla que sincroniza es la
 * trampa de R-16, y aqui no hace falta porque el dato se recalcula exacto.
 */
export function accumulationWeeksLeftOf(
  phase: BlockPhase,
  weekIndex: number,
  weeks?: number,
): number {
  return weekParamsForPhase(phase, weeks).filter(
    (semana) => semana.intent === 'accumulation' && semana.weekIndex > weekIndex,
  ).length;
}

function objetivo(reps: readonly [number, number], rir: number): RepTarget {
  return { repsMin: reps[0], repsMax: reps[1], rir };
}

function intencion(indice: number, acumulacion: number, ramp: WeekRamp): WeekIntent {
  if (indice < ramp.reintroductionWeeks) return 'reintroduction';
  if (ramp.intensificationWeeks > 0 && indice >= acumulacion - ramp.intensificationWeeks) {
    return 'intensification';
  }
  return 'accumulation';
}

// ---------------------------------------------------------------------------
// Plantilla y construccion por fase
// ---------------------------------------------------------------------------

export interface PhaseBlockOptions extends BuildBlockInput {
  /** Split pedido. Si no tiene plantillas se cae a `torso_leg` con aviso. */
  split?: Split | undefined;
  /** Sesiones de la IA, ya validadas. Ver `BlockTemplateOptions.sessions`. */
  sessions?: readonly SessionTemplate[] | undefined;
  /** Disponibilidad del atleta. Ver `BlockTemplateOptions.availability`. */
  availability?: Availability | null | undefined;
}

/**
 * `| undefined` explicito en los dos campos, y no solo `?`.
 *
 * Con `exactOptionalPropertyTypes` —que este repositorio tiene activado— «?»
 * significa AUSENTE, no «presente valiendo undefined». Sin el `| undefined`,
 * reenviar aqui un opcional que puede venir sin valor no compila, que es
 * exactamente lo que hace `buildPhaseBlock`.
 */
export interface BlockTemplateOptions {
  split?: Split | undefined;
  weeks?: number | undefined;
  /**
   * Sesiones que sustituyen a las de la plantilla del split.
   *
   * Es por donde entra el diseño de la IA, ya validado por
   * `validateSessionPlan`. El tipo es el MISMO que produce `splits.ts`
   * —`SessionTemplate[]`— y no uno paralelo: en cuanto hubiera dos formas de
   * describir una sesion, `buildBlock` tendria que saber de cual viene, y
   * ahora mismo no sabe ni le hace falta.
   *
   * Ausente o vacio = manda la plantilla. Un array vacio se trata como ausente
   * a proposito: un bloque sin sesiones no es un bloque, y caer a la plantilla
   * es mejor respuesta que construir uno vacio.
   */
  sessions?: readonly SessionTemplate[] | undefined;
  /**
   * Lo que el atleta declaró en el test inicial (docs/TEST_INICIAL.md §2.2).
   *
   * Si está, manda sobre el split pedido y sobre la plantilla por defecto de
   * la fase: el número de días elige las plantillas (`sessionsForDays`) y la
   * duración declarada recorta la de la fase —nunca la sube: el tope de la
   * fase también es un límite—. Las sesiones de la IA siguen mandando sobre
   * todo, porque ya se le pidió ese mismo número de sesiones y el validador lo
   * comprobó.
   *
   * `null` o ausente = un perfil anterior al test: se comporta como siempre.
   */
  availability?: Availability | null | undefined;
}

/**
 * Plantilla completa de un bloque de esa fase, ya cruzada con el split.
 *
 * La fase pone los parametros de semana, los objetivos y las metricas de
 * salida; el split pone las sesiones. Si el split pedido no tiene plantillas
 * escritas se usa `torso_leg`: es preferible un bloque construido con el split
 * de referencia que ninguno.
 */
export function blockTemplateFor(
  phase: BlockPhase,
  options: BlockTemplateOptions = {},
): BlockTemplate {
  const perfil = PHASE_RAMPS[phase];
  const disponibilidad = options.availability ?? null;
  const porDias =
    disponibilidad === null ? null : sessionsForDays(disponibilidad.days.length);
  const pedido = porDias?.split ?? options.split ?? perfil.defaultSplit;
  const split = hasTemplates(pedido) ? pedido : DEFAULT_SPLIT;
  const delSplit =
    porDias?.sessions ?? sessionsForSplit(split) ?? sessionsForSplit(DEFAULT_SPLIT) ?? [];
  const maxSessionMinutes =
    disponibilidad === null
      ? perfil.maxSessionMinutes
      : Math.min(perfil.maxSessionMinutes, disponibilidad.maxSessionMinutes);

  /*
   * Las sesiones de la IA mandan sobre las de la plantilla, y tambien sobre la
   * tabla de readaptacion de mas abajo.
   *
   * Que gobiernen tambien la readaptacion es deliberado: el §3 tabula sus
   * PARAMETROS DE SEMANA —series, RIR, reintroduccion—, que es lo que hace que
   * una readaptacion sea una readaptacion, y esos siguen saliendo de
   * `READAPTATION_TEMPLATE.weekParams` pase lo que pase. Lo que se puede
   * cambiar es QUE EJERCICIOS se hacen, que el documento no fija y que depende
   * del gimnasio y de la persona.
   */
  const disenadas = options.sessions !== undefined && options.sessions.length > 0;
  const sessions = disenadas ? [...(options.sessions ?? [])] : delSplit;

  // La readaptacion en su split de referencia ES la plantilla del §3, con sus
  // objetivos de sesion redactados en clave de reintroduccion.
  // Solo con las CUATRO sesiones de la plantilla: con cinco días, la quinta no
  // tiene objetivo de reintroducción escrito y se usan las del split tal cual.
  if (
    phase === 'readaptation' &&
    split === READAPTATION_TEMPLATE.split &&
    (disenadas || delSplit.length === READAPTATION_TEMPLATE.sessions.length)
  ) {
    return {
      ...READAPTATION_TEMPLATE,
      maxSessionMinutes: Math.min(READAPTATION_TEMPLATE.maxSessionMinutes, maxSessionMinutes),
      weekParams: weekParamsForPhase(phase, options.weeks),
      ...(disenadas ? { sessions } : {}),
    };
  }

  return {
    name: perfil.label,
    phase,
    split,
    maxSessionMinutes,
    objectives: perfil.objectives,
    exitMetrics: perfil.exitMetrics,
    weekParams: weekParamsForPhase(phase, options.weeks),
    sessions,
  };
}

/** Construye un bloque de cualquier fase contra el equipamiento del usuario. */
export function buildPhaseBlock(phase: BlockPhase, options: PhaseBlockOptions): BuiltBlock {
  const pedido = options.split ?? PHASE_RAMPS[phase].defaultSplit;
  const template = blockTemplateFor(phase, {
    split: pedido,
    weeks: options.weeks,
    sessions: options.sessions,
    availability: options.availability,
  });
  const block = buildBlock(template, options);

  // Con disponibilidad el split lo eligen los días, no el pedido: no hay nada
  // que avisar aunque el pedido no tenga plantillas.
  if (!hasTemplates(pedido) && (options.availability ?? null) === null) {
    // Se avisa en lugar de fallar, pero se avisa SIEMPRE: el usuario pidio un
    // split y ha recibido otro.
    block.warnings.unshift(
      `El split «${pedido}» no tiene plantillas: se ha construido con «${template.split}»`,
    );
  }

  return block;
}
